import { api } from '@/lib/api-client'
import type {
PrintAgent,
Printer,
PrintJob,
PrintTemplate,
PrintTemplateVariant,
CreatePrintAgentPayload,
UpdatePrintAgentPayload,
CreatePrinterPayload,
UpdatePrinterPayload,
CreatePrintJobReceiptPayload,
CreatePrintJobLabelPayload,
CreatePrintJobRawPayload,
CreatePrintTemplatePayload,
UpdatePrintTemplatePayload,
CreatePrintTemplateVariantPayload,
UpdatePrintTemplateVariantPayload,
RenderTemplatePayload,
ValidateReceiptHtmlPayload,
ValidateReceiptHtmlResult,
} from '@/types'

export interface PaginatedData<T> {
  data: T[]
  meta: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

export interface PaginationParams {
  page?: number
  limit?: number
  search?: string
}

// ─── Print Agents ──────────────────────────────────────

export async function getPrintAgents(
  shopId: string,
  params?: PaginationParams
): Promise<PaginatedData<PrintAgent>> {
  return api.get<PaginatedData<PrintAgent>>('/printer/print-agents', {
    params: { shopId, ...params },
  })
}

export async function getPrintAgent(id: string): Promise<PrintAgent> {
  return api.get<PrintAgent>(`/printer/print-agents/${id}`)
}

export async function createPrintAgent(
  data: CreatePrintAgentPayload & { shopId: string }
): Promise<PrintAgent & { pairCode: string }> {
  return api.post<PrintAgent & { pairCode: string }>('/printer/print-agents', data)
}

export async function updatePrintAgent(
  id: string,
  data: UpdatePrintAgentPayload
): Promise<PrintAgent> {
  return api.put<PrintAgent>(`/printer/print-agents/${id}`, data)
}

export async function deletePrintAgent(id: string): Promise<void> {
  return api.delete<void>(`/printer/print-agents/${id}`)
}

export async function regeneratePairCode(
  id: string
): Promise<{ pairCode: string; pairCodeExpiresAt: string }> {
  return api.post(`/printer/print-agents/${id}/regenerate-pair-code`)
}

export async function unpairAgent(id: string): Promise<void> {
  return api.post(`/printer/print-agents/${id}/unpair`)
}

export async function regenerateAgentKey(
  id: string
): Promise<{ apiKey: string }> {
  return api.post(`/printer/print-agents/${id}/regenerate-key`)
}

// ─── Printers ──────────────────────────────────────────

export interface PrinterParams extends PaginationParams {
  type?: string
}

export async function getPrinters(
  shopId: string,
  params?: PrinterParams
): Promise<PaginatedData<Printer>> {
  return api.get<PaginatedData<Printer>>('/printer/printers', {
    params: { shopId, ...params },
  })
}

export async function getPrinter(id: string): Promise<Printer> {
  return api.get<Printer>(`/printer/printers/${id}`)
}

export async function createPrinter(
  data: CreatePrinterPayload & { shopId: string }
): Promise<Printer> {
  return api.post<Printer>('/printer/printers', data)
}

export async function updatePrinter(
  id: string,
  data: UpdatePrinterPayload
): Promise<Printer> {
  return api.put<Printer>(`/printer/printers/${id}`, data)
}

export async function deletePrinter(id: string): Promise<void> {
  return api.delete<void>(`/printer/printers/${id}`)
}

export async function testPrinter(
  id: string
): Promise<{ jobId: string; status: string }> {
  return api.post(`/printer/printers/${id}/test`)
}

// ─── Print Jobs ────────────────────────────────────────

export interface PrintJobParams extends PaginationParams {
  status?: string
  type?: string
  printerId?: string
}

export async function getPrintJobs(
  shopId: string,
  params?: PrintJobParams
): Promise<PaginatedData<PrintJob>> {
  return api.get<PaginatedData<PrintJob>>('/printer/print-jobs', {
    params: { shopId, ...params },
  })
}

export async function getPrintJob(id: string): Promise<PrintJob> {
  return api.get<PrintJob>(`/printer/print-jobs/${id}`)
}

export async function createReceiptJob(
  data: CreatePrintJobReceiptPayload
): Promise<PrintJob> {
  return api.post<PrintJob>('/printer/print-jobs/receipt', data)
}

export async function createLabelJob(
  data: CreatePrintJobLabelPayload
): Promise<PrintJob> {
  return api.post<PrintJob>('/printer/print-jobs/label', data)
}

export async function createRawJob(
  data: CreatePrintJobRawPayload
): Promise<PrintJob> {
  return api.post<PrintJob>('/printer/print-jobs/raw', data)
}

export async function retryPrintJob(id: string): Promise<PrintJob> {
  return api.post<PrintJob>(`/printer/print-jobs/${id}/retry`)
}

export async function cancelPrintJob(id: string): Promise<PrintJob> {
  return api.post<PrintJob>(`/printer/print-jobs/${id}/cancel`)
}

// ─── Print Templates ───────────────────────────────────

export interface PrintTemplateParams extends PaginationParams {
  type?: string
  shopId?: string
  isDefault?: boolean
  isSystem?: boolean
  isActive?: boolean
  scope?: 'system' | 'organization' | 'shop'
}

export async function getPrintTemplates(
  params?: PrintTemplateParams
): Promise<PaginatedData<PrintTemplate>> {
  return api.get<PaginatedData<PrintTemplate>>('/printer/print-templates', {
    params,
  })
}

export async function getPrintTemplate(id: string): Promise<PrintTemplate> {
  return api.get<PrintTemplate>(`/printer/print-templates/${id}`)
}

export async function createPrintTemplate(
  data: CreatePrintTemplatePayload
): Promise<PrintTemplate> {
  return api.post<PrintTemplate>('/printer/print-templates', data)
}

export async function updatePrintTemplate(
  id: string,
  data: UpdatePrintTemplatePayload
): Promise<PrintTemplate> {
  return api.put<PrintTemplate>(`/printer/print-templates/${id}`, data)
}

export async function deletePrintTemplate(id: string): Promise<void> {
  return api.delete<void>(`/printer/print-templates/${id}`)
}

export async function setDefaultTemplate(id: string): Promise<PrintTemplate> {
  return api.post<PrintTemplate>(`/printer/print-templates/${id}/set-default`)
}

/// Copies a template (including read-only system defaults) into the caller's
/// organization as an editable starting point.
export async function duplicatePrintTemplate(
  id: string,
  data?: { name?: string; shopId?: string | null }
): Promise<PrintTemplate> {
  return api.post<PrintTemplate>(`/printer/print-templates/${id}/duplicate`, data ?? {})
}

export async function renderTemplate(
  data: RenderTemplatePayload
): Promise<{ rendered: string }> {
  return api.post<{ rendered: string }>('/printer/print-templates/render', data)
}

// ─── Print Template Variants ───────────────────────────

export async function createPrintTemplateVariant(
  templateId: string,
  data: CreatePrintTemplateVariantPayload
): Promise<PrintTemplateVariant> {
  return api.post<PrintTemplateVariant>(
    `/printer/print-templates/${templateId}/variants`,
    data
  )
}

export async function updatePrintTemplateVariant(
  templateId: string,
  variantId: string,
  data: UpdatePrintTemplateVariantPayload
): Promise<PrintTemplateVariant> {
  return api.put<PrintTemplateVariant>(
    `/printer/print-templates/${templateId}/variants/${variantId}`,
    data
  )
}

export async function deletePrintTemplateVariant(
  templateId: string,
  variantId: string
): Promise<void> {
  return api.delete<void>(
    `/printer/print-templates/${templateId}/variants/${variantId}`
  )
}

// ─── ESC/POS Render ───────────────────────────────────

export interface RenderEscPosOptions {
  html: string
  paperWidthMm: 58 | 80
  cut?: boolean
  openDrawer?: boolean
  language?: 'esc-pos' | 'star-prnt'
}

export async function renderEscPos(options: RenderEscPosOptions): Promise<Uint8Array> {
  const { data } = await api.post<{ data: string }>(
    '/printer/print-templates/render-escpos',
    options
  )
  const binary = atob(data)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

// ─── Receipt Image Render ─────────────────────────────

export interface RenderReceiptImageOptions {
  html: string
  paperWidthMm?: 58 | 80
}

export interface RenderReceiptImageResult {
  image: string
  width: number
  height: number
}

export async function renderReceiptImage(
  options: RenderReceiptImageOptions
): Promise<RenderReceiptImageResult> {
  return api.post<RenderReceiptImageResult>(
    '/printer/print-templates/render-receipt-image',
    options
  )
}

// ─── Receipt HTML Validation + Preview ───────────────

export async function validateReceiptHtml(
  payload: ValidateReceiptHtmlPayload
): Promise<ValidateReceiptHtmlResult> {
  return api.post<ValidateReceiptHtmlResult>(
    '/printer/print-templates/validate-receipt-html',
    payload
  )
}

// ─── ZPL Render ───────────────────────────────────────

export interface RenderZplOptions {
  widthMm?: number
  heightMm?: number
  dpmm?: number
}

export async function renderZpl(
  zpl: string,
  options: RenderZplOptions = {}
): Promise<string> {
  const { widthMm = 101.6, heightMm = 152.4, dpmm = 8 } = options
  // Convert dpmm to approximate DPI for backend (1 dpmm = 25.4 DPI)
  const dpi = Math.round(dpmm * 25.4)
  const result = await api.post<{ image: string }>(
    '/printer/print-templates/render-label-image',
    { zpl, widthMm, heightMm, dpi }
  )
  return `data:image/png;base64,${result.image}`
}
